import {
  FLUTE_TYPES,
  PLY_MATERIALS,
  VEHICLES,
  PACKING_EFFICIENCY,
} from './constants';
import type { BoxForm, Ply, Vendor, PrintConfig } from './types';

/**
 * buildParsePrompt
 * Page 1 → 2: Claude extracts box specs from a free-text description.
 */
export function buildParsePrompt(description: string): string {
  const materialList = PLY_MATERIALS.map((m) => `"${m}"`).join('|');

  return `You are a corrugated box specification parser. Extract specs from the description.

DESCRIPTION: "${description}"

Return JSON only, no markdown:
{
  "length": <cm or null>, "width": <cm or null>, "height": <cm or null>,
  "quantity": <number or null>, "delivery_location": "<city or null>",
  "plies": [{ "type":"<liner|flute>","material":"<${materialList}|null>","gsm":<n or null>,"fluteType":"<A-Flute|B-Flute|C-Flute|E-Flute|F-Flute|null>" }],
  "parse_notes": "<1 sentence summary>"
}
Rules: convert units to cm; plies alternate liner/flute/liner min 3; default to 3-ply if not mentioned; fluteType null for liners; if no flute type specified use your assumption;leave nulls if not mentioned.`;
}

interface BuildPricingArgs {
  form: BoxForm;
  plies: Ply[];
  vendors: Vendor[];
  printConfig: PrintConfig;
}

/**
 * buildPricingPrompt
 * Page 2 → 3: Claude calculates the full delivered price per vendor
 * including material, volume-based shipping, and printing costs.
 */
export function buildPricingPrompt({
  form,
  plies,
  vendors,
  printConfig,
}: BuildPricingArgs): string {
  const { length, width, height, location, quantity } = form;
  const { printRatePerKg, colourMultiplier, colourLabel } = printConfig;

  const plyLines = plies
    .map((p, i) => {
      const gsm = p.gsm ? `GSM:${p.gsm}` : `GSM:NOT PROVIDED(AI must assume)`;
      const flute =
        p.type === 'flute'
          ? p.fluteType
            ? `Flute:${p.fluteType}(×${FLUTE_TYPES[p.fluteType]?.takeup})`
            : `Flute:NOT PROVIDED(AI must assume)`
          : '';
      return `  L${i + 1}: ${p.type === 'liner' ? 'LINER' : 'FLUTE'} | Mat:${p.material} | ${gsm}${flute ? ' | ' + flute : ''}`;
    })
    .join('\n');

  const vendorLines = vendors
    .map(
      (v, i) =>
        `  ${i + 1}. ID:${v.id} | Name:${v.name} | City:${v.city} | Rating:${v.rating}` +
        ` | Paper:₹${v.paper_rate}/kg | Conversion:₹${v.conversion_rate}/kg`
    )
    .join('\n');

  const vehicleLines = VEHICLES.map(
    (v) => `  ${v.name}: cargo ${v.cargo_m3}m³ | ₹${v.rate_per_km}/km`
  ).join('\n');

  const fluteTable = Object.entries(FLUTE_TYPES)
    .map(([k, v]) => `${k}:×${v.takeup}`)
    .join(' | ');

  return `You are a corrugated box pricing expert. Calculate the FULL delivered price for EACH vendor including material, shipping, and printing costs.

BOX: ${length}cm × ${width}cm × ${height}cm | Qty: ${quantity} | Delivery: ${location}

PLY STRUCTURE (Layer 1 = outermost liner used for printing):
${plyLines}

VENDORS:
${vendorLines}

VEHICLE OPTIONS (pick cheapest that fits full order in one trip):
${vehicleLines}
Packing efficiency: ${PACKING_EFFICIENCY} (accounts for stacking gaps)

PRINT CONFIG: ${colourLabel} | multiplier: ${colourMultiplier} | rate: ₹${printRatePerKg}/kg

═══════════ EXACT FORMULA ═══════════

STEP 1 — Box blank area:
  blank_cm2 = (2×${length} + 2×${width} + 2) × (${width} + ${height} + 1)
  blank_m2  = blank_cm2 / 10000

STEP 2 — Layer weights:
  Liner weight_g = blank_m2 × GSM
  Flute weight_g = blank_m2 × GSM × takeup_factor
  Takeups: ${fluteTable}
  total_box_weight_g  = sum of all layer weights
  total_box_weight_kg = total_box_weight_g / 1000

STEP 3 — Material cost per unit (per vendor):
  material_cost_per_unit = total_box_weight_kg × (paper_rate_per_kg + conversion_cost_per_kg)

STEP 4 — Flat-packed volume:
  box_face_area_m2  = (${length}/100) × (${width}/100)
  thickness_mm      = estimate from ply structure and GSM
                      (base: 3-ply~4mm, 5-ply~7mm, 7-ply~10mm, scaled by avg liner GSM vs 150gsm baseline)
  volume_per_box_m3 = box_face_area_m2 × (thickness_mm / 1000)
  total_volume_m3   = (volume_per_box_m3 × ${quantity}) / ${PACKING_EFFICIENCY}

STEP 5 — Vehicle selection and shipping cost (same vehicle for all vendors):
  Select cheapest vehicle where cargo_m3 >= total_volume_m3
  If exceeds all vehicles, use 20ft truck and note overflow
  FOR EACH VENDOR:
    IF vendor city == "${location}": distance_km = 0, shipping_total = 0
    ELSE: estimate realistic road distance in km between vendor city and "${location}"
    shipping_total    = selected_vehicle.rate_per_km × distance_km
    shipping_per_unit = shipping_total / ${quantity}

STEP 6 — Printing cost per unit (same for all vendors):
  outer_liner_gsm        = GSM of Layer 1 (outermost liner)
  outer_liner_weight_kg  = blank_m2 × outer_liner_gsm / 1000
  print_cost_per_unit    = outer_liner_weight_kg × ${printRatePerKg} × ${colourMultiplier}
  (0 when colour_multiplier is 0)

STEP 7 — Final delivered price:
  price_per_unit = material_cost_per_unit + shipping_per_unit + print_cost_per_unit
  total_price    = price_per_unit × ${quantity}

ASSUMPTIONS: liner GSM missing→150gsm, flute GSM missing→120gsm, flute type missing→C-Flute(×1.45). State all.
Only show top 3 vendors based on cheapest price and distance
Also always add the vendor which is closest to the delivery location
Note: delivery time will increase with distance So need to keep balance of both price and timelines

Return JSON ONLY — no markdown:
{
  "assumptions": [{"layer":<n>,"field":"<gsm|flute_type>","assumed_value":"<v>","reason":"<why>"}],
  "box_calculation": {
    "blank_area_cm2":<n>, "blank_area_m2":<n>,
    "layers": [{"layer":<n>,"role":"<Liner|Flute>","material":"<m>","gsm":<n>,"takeup":<n>,"weight_g":<n>}],
    "total_box_weight_g":<n>, "total_box_weight_kg":<n>
  },
  "volume_calculation": {
    "box_face_area_m2":<n>, "estimated_thickness_mm":<n>, "thickness_reasoning":"<brief>",
    "volume_per_box_m3":<n>, "total_volume_m3":<n>,
    "selected_vehicle":"<n>", "vehicle_cargo_m3":<n>, "vehicle_rate_per_km":<n>
  },
  "print_calculation": {
    "outer_liner_gsm":<n>, "outer_liner_weight_kg":<n>,
    "colour_label":"<l>", "colour_multiplier":<n>,
    "print_rate_per_kg":<n>, "print_cost_per_unit":<n>
  },
  "vendor_quotes": [{
    "id":"<id>", "name":"<n>", "factory_location":"<l>", "rating":<n>,
    "paper_rate_per_kg":<n>, "conversion_cost_per_kg":<n>, "material_cost_per_unit":<n>,
    "distance_km":<n>, "shipping_total":<n>, "shipping_per_unit":<n>,
    "print_cost_per_unit":<n>, "price_per_unit":<n>, "total_price":<n>
  }],
  "recommended_vendor":"<n>",
  "recommendation_reason":"<1-2 sentences>",
  "cost_saving_tip":"<tip or empty>"
}`;
}
